import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  edrService,
  CompanyOption,
  NoticePreview,
  ThreatNoticeList,
} from '../../../services/edr.service';
import { formatPeriod, recentPeriods } from '../../../lib/edrPeriod';
import { blockReviewer } from './edrAdminGuard';

/**
 * PRD [S6] 관리자 — 위협탐지 HTML 등록
 *
 * 저장 **전**에 파싱 결과와 살균된 렌더 화면을 함께 보여주는 것이 이 화면의 요점이다.
 * 미리보기를 거치지 않으면 등록 버튼 자체가 나타나지 않는다 — 담당자가 확인하지 않은
 * HTML이 그대로 적재되는 경로를 만들지 않는다.
 *
 * 항목이 누락돼도 **등록은 허용**한다. 안랩 메일 서식이 바뀌면 일부 필드가 빠질 수 있고,
 * 그때 등록을 막으면 위협 안내 자체가 올라가지 못한다. `parse_status = PARTIAL`로 남긴다.
 */

const RISK_CLASS: Record<string, string> = {
  높음: 'badge-risk-high',
  중간: 'badge-risk-mid',
  낮음: 'badge-risk-low',
};

const PARSE_STATUS_OPTIONS = ['OK', 'PARTIAL'] as const;

/** sha256 64자는 표 폭을 밀어낸다 — 앞뒤만 남기고 전문은 title에 둔다 */
const shortenHash = (hash: string): string =>
  hash.length <= 20 ? hash : `${hash.slice(0, 8)}…${hash.slice(-8)}`;

/** 파싱 결과 표의 행 — data-field 순서는 정의서 v2.0을 따른다 */
type ParseRow = { field: string; label: string; value: React.ReactNode; note: string };

const buildParseRows = (p: NoticePreview): ParseRow[] => {
  const assetNote = [
    p.assetHost ? `호스트: ${p.assetHost}` : null,
    p.assetAccount ? `계정: ${p.assetAccount}` : null,
    p.assetOs ? `OS: ${p.assetOs}` : null,
  ]
    .filter(Boolean)
    .join(' / ');

  return [
    {
      field: 'affiliate',
      label: '대상 계열사',
      value: p.affiliateRaw,
      // 매칭 결과를 함께 보여준다 — 매핑에 없는 값이면 등록 후 조회에서 사라진다
      note: p.companyName ? `→ ${p.companyName}` : '매칭되는 계열사가 없습니다',
    },
    {
      field: 'risk_level',
      label: '위험도',
      value: (
        <span className={`inline-badge ${RISK_CLASS[p.riskLevel] ?? ''}`}>{p.riskLevel}</span>
      ),
      note: '',
    },
    { field: 'risk_type', label: '위험 유형', value: p.riskType, note: p.riskTypeNote ?? '' },
    // `detectedAt`은 UTC ISO다 — 서버가 만든 표시 문자열을 쓴다
    { field: 'detected_at', label: '탐지 일시', value: p.detectedAtDisplay, note: 'KST' },
    { field: 'asset', label: '대상 자산', value: p.assetIp, note: assetNote },
    {
      field: 'detection_scenario',
      label: '탐지 시나리오',
      value: p.detectionScenario,
      note: p.detectionNote ?? '',
    },
    { field: 'source_file', label: '원인 파일', value: p.sourceFile, note: '' },
  ];
};

const EdrNoticeAdmin: React.FC<{ isReviewer: boolean }> = ({ isReviewer }) => {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [list, setList] = useState<ThreatNoticeList | null>(null);

  const [html, setHtml] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<NoticePreview | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [filterCompany, setFilterCompany] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [filterParseStatus, setFilterParseStatus] = useState('');
  const [page, setPage] = useState(1);

  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const periods = useMemo(() => recentPeriods(new Date(), 12), []);

  const loadList = useCallback(async () => {
    try {
      const data = await edrService.getThreatNotices({
        companyName: filterCompany || undefined,
        period: filterPeriod || undefined,
        page,
      });
      // 파싱 상태는 조회 API 파라미터가 아니다 — 받은 결과에서 걸러낸다
      setList(
        filterParseStatus
          ? { ...data, rows: data.rows.filter((r) => r.parseStatus === filterParseStatus) }
          : data,
      );
    } catch {
      setMessage({ type: 'error', text: '등록 목록을 불러오지 못했습니다.' });
    }
  }, [filterCompany, filterPeriod, filterParseStatus, page]);

  useEffect(() => {
    edrService
      .getCompanies('EDR_THREAT')
      .then(setCompanies)
      .catch(() => setMessage({ type: 'error', text: '회사 목록을 불러오지 못했습니다.' }));
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const reset = () => {
    setHtml('');
    setFile(null);
    setPreview(null);
    setEditingId(null);
    setMessage(null);
  };

  const handlePreview = async () => {
    if (!html.trim()) {
      setMessage({ type: 'error', text: 'HTML 소스를 입력하거나 파일을 업로드해주세요.' });
      return;
    }

    setIsBusy(true);
    setMessage(null);
    try {
      setPreview(await edrService.previewThreatNotice(html));
    } catch (error: any) {
      setPreview(null);
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || '파싱에 실패했습니다. HTML 서식을 확인해주세요.',
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleRegister = async () => {
    if (blockReviewer(isReviewer)) return;

    setIsBusy(true);
    try {
      if (editingId !== null) {
        await edrService.updateThreatNotice(editingId, html);
        setMessage({ type: 'success', text: '수정되었습니다.' });
      } else {
        await edrService.registerThreatNotice(html);
        setMessage({ type: 'success', text: '등록되었습니다.' });
      }
      setHtml('');
      setPreview(null);
      setEditingId(null);
      await loadList();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || '저장에 실패했습니다.',
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: '.html 파일을 선택해주세요.' });
      return;
    }
    if (blockReviewer(isReviewer)) return;

    setIsBusy(true);
    try {
      await edrService.uploadThreatNotice(file);
      setMessage({ type: 'success', text: '등록되었습니다.' });
      setFile(null);
      await loadList();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || '업로드에 실패했습니다.',
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleEdit = async (id: number) => {
    setIsBusy(true);
    setPreview(null);
    try {
      const { html: raw } = await edrService.getThreatNoticeRaw(id);
      setHtml(raw);
      setEditingId(id);
      setMessage({ type: 'success', text: '원본을 불러왔습니다. 미리보기 후 저장하세요.' });
    } catch {
      setMessage({ type: 'error', text: '원본 HTML을 불러오지 못했습니다.' });
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 위협탐지 안내를 삭제하시겠습니까?')) return;
    if (blockReviewer(isReviewer)) return;

    try {
      await edrService.deleteThreatNotice(id);
      setMessage({ type: 'success', text: '삭제되었습니다.' });
      await loadList();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || '삭제에 실패했습니다.',
      });
    }
  };

  const totalPages = list ? Math.max(1, Math.ceil(list.total / list.pageSize)) : 1;

  return (
    <div className="edr-notice-admin">
      <h2>위협탐지 HTML 등록</h2>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* ── 1) 입력 ─────────────────────────────────────────── */}
      <h3>1) HTML 입력</h3>
      <div className="edr-notice-input">
        <div className="form-group edr-notice-input__paste">
          <label htmlFor="edr-notice-html">HTML 소스 붙여넣기</label>
          <textarea
            id="edr-notice-html"
            className="summary-editor"
            rows={8}
            value={html}
            disabled={isBusy}
            placeholder="<html> … 안내 메일 HTML 전문을 붙여넣습니다 …"
            onChange={(e) => setHtml(e.target.value)}
          />
        </div>
        <div className="form-group edr-notice-input__file">
          <label htmlFor="edr-notice-file">또는 파일 업로드</label>
          <input
            id="edr-notice-file"
            type="file"
            accept=".html,.htm"
            disabled={isBusy}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <button className="btn-secondary" onClick={handleUpload} disabled={!file || isBusy}>
            파일 등록
          </button>
          <p className="edr-hint">
            파일 등록은 미리보기 없이 바로 저장된다. 파싱 결과를 확인하려면 소스를 붙여넣는다.
          </p>
        </div>
      </div>
      <div className="edr-notice-actions">
        <button className="btn-primary" onClick={handlePreview} disabled={isBusy}>
          미리보기 (파싱)
        </button>
        <button className="btn-secondary" onClick={reset} disabled={isBusy}>
          초기화
        </button>
      </div>

      {/* ── 2) 파싱 결과 ────────────────────────────────────── */}
      {/* 미리보기 전에도 제목을 남긴다 — 없으면 번호가 1) → 3)으로 튄다 */}
      <h3>2) 파싱 결과 미리보기</h3>
      {!preview ? (
        <p className="no-data">HTML 소스를 붙여넣고 [미리보기 (파싱)]를 누르세요.</p>
      ) : (
        <>
          {preview.parseStatus === 'PARTIAL' && (
            <div className="message error" data-testid="edr-parse-warning">
              <span>
                <strong>항목 누락 {preview.missingFields.length}건</strong> —{' '}
                <code>{preview.missingFields.join(', ')}</code>을 찾지 못했습니다. 등록은 가능하며{' '}
                <code>parse_status = PARTIAL</code>로 저장됩니다.
              </span>
            </div>
          )}

          {preview.isDuplicate && (
            <div className="message error" data-testid="edr-duplicate-warning">
              <span>
                이미 등록된 건입니다 (같은 <code>content_hash</code>). 등록하면 기존 건이 갱신됩니다.
              </span>
            </div>
          )}

          <div className="table-container">
            <table className="data-table" aria-label="파싱 결과">
              <thead>
                <tr>
                  <th>No</th>
                  <th>data-field</th>
                  <th>표시 라벨</th>
                  <th>추출 값</th>
                  <th>부가설명</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {buildParseRows(preview).map((row, idx) => (
                  <tr key={row.field}>
                    <td>{idx + 1}</td>
                    <td className="mono">{row.field}</td>
                    <td>{row.label}</td>
                    <td>{row.value || <span className="text-muted">(찾지 못함)</span>}</td>
                    <td>{row.note || <span className="text-muted">—</span>}</td>
                    <td>
                      {preview.missingFields.includes(row.field) ? (
                        <span className="inline-badge badge-parse-partial">누락</span>
                      ) : (
                        <span className="inline-badge badge-parse-ok">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="edr-notice-confirm">
            <div className="edr-notice-confirm__keys">
              <h4>저장 키</h4>
              <table className="policy-table" aria-label="저장 키">
                <tbody>
                  <tr>
                    <th>period</th>
                    <td className="mono">{preview.period}</td>
                  </tr>
                  <tr>
                    <th>company_name</th>
                    <td>{preview.companyName}</td>
                  </tr>
                  <tr>
                    <th>detected_date</th>
                    {/* 서버가 계산한 값 — 여기서 detectedAt을 자르면 KST 자정 건이 전날로 밀린다 */}
                    <td className="mono">{preview.detectedDate}</td>
                  </tr>
                  <tr>
                    <th>content_hash</th>
                    <td>
                      {/* 64자를 그대로 넣으면 표가 밀려 옆 배지가 화면 밖으로 나간다 */}
                      <span
                        className="mono"
                        data-testid="edr-content-hash"
                        title={preview.contentHash}
                      >
                        {shortenHash(preview.contentHash)}
                      </span>{' '}
                      <span
                        className={`inline-badge ${
                          preview.isDuplicate ? 'badge-parse-partial' : 'badge-active'
                        }`}
                      >
                        {preview.isDuplicate ? '중복' : '중복 없음'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="edr-notice-confirm__render">
              <h4>렌더 미리보기</h4>
              {/*
                살균은 백엔드 sanitizer가 이미 끝냈다 — `htmlSanitized`는 허용 태그만 남은
                결과물이며, 담당자가 최종 모습을 확인하는 것이 이 화면의 목적이다.
              */}
              <div
                className="edr-render-preview"
                data-testid="edr-render-preview"
                dangerouslySetInnerHTML={{ __html: preview.htmlSanitized }}
              />
            </div>
          </div>

          <div className="edr-notice-actions edr-notice-actions--end">
            <button className="btn-secondary" onClick={reset} disabled={isBusy}>
              취소
            </button>
            <button className="btn-primary" onClick={handleRegister} disabled={isBusy}>
              {editingId !== null ? '수정 저장' : '등록'}
            </button>
          </div>
        </>
      )}

      {/* ── 3) 등록 목록 ────────────────────────────────────── */}
      <h3>3) 등록 목록</h3>
      <div className="report-management-controls">
        <div className="control-group">
          <label htmlFor="edr-notice-filter-company">회사:</label>
          <select
            id="edr-notice-filter-company"
            value={filterCompany}
            onChange={(e) => {
              setFilterCompany(e.target.value);
              setPage(1);
            }}
          >
            <option value="">전체</option>
            {companies.map((c) => (
              <option key={c.orgCode} value={c.companyName}>
                {c.companyName}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="edr-notice-filter-period">기간:</label>
          <select
            id="edr-notice-filter-period"
            value={filterPeriod}
            onChange={(e) => {
              setFilterPeriod(e.target.value);
              setPage(1);
            }}
          >
            <option value="">전체</option>
            {periods.map((p) => (
              <option key={p} value={p}>
                {formatPeriod(p)}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="edr-notice-filter-parse">파싱 상태:</label>
          <select
            id="edr-notice-filter-parse"
            value={filterParseStatus}
            onChange={(e) => setFilterParseStatus(e.target.value)}
          >
            <option value="">전체</option>
            {PARSE_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>No</th>
              <th>탐지일자</th>
              <th>위험도</th>
              <th>진단명</th>
              <th>IP</th>
              <th>계열사</th>
              <th>파싱</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {!list || list.rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="no-data">
                  등록된 위협탐지 안내가 없습니다.
                </td>
              </tr>
            ) : (
              list.rows.map((row, idx) => (
                <tr key={row.id}>
                  <td>{(list.page - 1) * list.pageSize + idx + 1}</td>
                  <td>{row.detectedDate}</td>
                  <td>
                    <span className={`inline-badge ${RISK_CLASS[row.riskLevel] ?? ''}`}>
                      {row.riskLevel}
                    </span>
                  </td>
                  <td className="mono">{row.detectionScenario}</td>
                  <td className="mono">{row.assetIp}</td>
                  <td>{row.companyName}</td>
                  <td>
                    <span
                      className={`inline-badge ${
                        row.parseStatus === 'OK' ? 'badge-parse-ok' : 'badge-parse-partial'
                      }`}
                    >
                      {row.parseStatus}
                    </span>
                  </td>
                  <td className="action-buttons">
                    <button
                      className="btn-primary"
                      onClick={() => handleEdit(row.id)}
                      disabled={isBusy}
                    >
                      수정
                    </button>
                    <button
                      className="btn-danger"
                      onClick={() => handleDelete(row.id)}
                      disabled={isBusy}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {list && list.total > list.pageSize && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            이전
          </button>
          <span className="pagination-info">
            {page} / {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            다음
          </button>
        </div>
      )}
    </div>
  );
};

export default EdrNoticeAdmin;
